import React from 'react';
import * as Yup from 'yup';
import { TextInput, RadioGroup } from '@youngcapital/component-library';
import { validName, validCandidateAge } from '@youngcapital/validations';
import i18next from 'i18next';

import { asFieldset, FIELDSET_PROPTYPES } from '../asFieldset';
import ResponsiveDatePicker from '../../ResponsiveDatePicker';
import { getRadioProps } from '../helpers/utils';
import Blokkendoos from '../../../services/blokkendoos';

const CURRENT_YEAR = new Date().getFullYear();
const MAX_YEAR = CURRENT_YEAR - (isGermanSite() ? 16 : 13);
const MIN_YEAR = CURRENT_YEAR - 100;

const BinaryGenderField = ({ t, getFieldProps }) => (
  <RadioGroup
    label={t('fieldset_personal_info.gender_label')}
    {...getRadioProps('gender', getFieldProps)}
    items={[
      {
        value: 'male',
        label: t('fieldset_personal_info.gender_male_label')
      },
      {
        value: 'female',
        label: t('fieldset_personal_info.gender_female_label')
      },
      {
        value: 'other',
        label: t('fieldset_personal_info.gender_other_label')
      }
    ]}
    isRequired
  />
);

BinaryGenderField.propTypes = FIELDSET_PROPTYPES;
const DIVERSE_FIELDS = t => [
  {
    value: 'male',
    label: t('fieldset_personal_info.gender_male_label')
  },
  {
    value: 'female',
    label: t('fieldset_personal_info.gender_female_label')
  },
  {
    value: 'other',
    label: t('fieldset_personal_info.gender_other_label')
  }
];

const DiverseGenderField = ({ t, getFieldProps }) => (
  <div data-testid='diverse-gender' className='input input--select'>
    <label htmlFor='gender'>
      {t('fieldset_personal_info.gender_label_optional')}
    </label>
    <select
      as='select'
      name='gender'
      className='input__control input__select'
      {...getFieldProps('gender', props => ({
        ...props,
        onChange: e => {
          const { value } = e.target;
          props.setValue(value);
        }
      }))}
    >
      <option
        disabled
        selected
        value=''
        label={t('fieldset_personal_info.gender_select')}
      />
      {DIVERSE_FIELDS(t).map((field, i) => (
        <option key={`gender-${i}`} value={field.value}>
          {field.label}
        </option>
      ))}
    </select>
  </div>
);

DiverseGenderField.propTypes = FIELDSET_PROPTYPES;

const PersonalInfoFieldset = ({ getFieldProps, t, options = {} }) => {
  const o = defaultOptions(options);

  const genderField = options.diverse_gender_options ? (
    <DiverseGenderField getFieldProps={getFieldProps} t={t} />
  ) : (
    <BinaryGenderField getFieldProps={getFieldProps} t={t} />
  );

  return (
    <>
      <h3>{t('fieldset_personal_info.header')}</h3>
      <TextInput
        label={t('fieldset_personal_info.first_name_label')}
        {...getFieldProps('firstname')}
        isRequired
      />
      <TextInput
        label={t('fieldset_personal_info.last_name_label')}
        {...getFieldProps('lastname')}
        isRequired
      />
      {o.gender && genderField}
      <ResponsiveDatePicker
        fieldProps={getFieldProps('date_of_birth')}
        label={t(
          options.dob_optional
            ? 'fieldset_personal_info.date_of_birth_label_optional'
            : 'fieldset_personal_info.date_of_birth_label'
        )}
        maxYear={MAX_YEAR}
        minYear={MIN_YEAR}
        isRequired={!options.dob_optional}
      />
    </>
  );
};

PersonalInfoFieldset.type = 'personal-info';

PersonalInfoFieldset.propTypes = FIELDSET_PROPTYPES;

PersonalInfoFieldset.initialValues = (options = {}) => {
  const o = defaultOptions(options);
  const values = {
    firstname: '',
    lastname: '',
    date_of_birth: ''
  };
  if (o.gender) {
    values.gender = null;
  }
  if (o.diverse_gender_options) {
    values.gender = null;
    values.diverse_gender_options = true;
  }
  return values;
};

PersonalInfoFieldset.validationSchema = (options = {}) => {
  const o = defaultOptions(options);
  const schema = {
    firstname: validName().required(),
    lastname: validName().required(),
    date_of_birth: options.dob_optional
      ? validCandidateAge(i18next.language)
      : validCandidateAge(i18next.language).required()
  };
  if (o.gender && !o.diverse_gender_options) {
    schema.gender = Yup.mixed().required();
  }
  return schema;
};

function isGermanSite() {
  const siteAbbr = new Blokkendoos().getSiteAbbr();
  return ['YC_DE', 'SJ_DE', 'NJ_DE'].includes(siteAbbr);
}

function defaultOptions(options) {
  return {
    gender: true,
    ...options
  };
}

export default asFieldset(PersonalInfoFieldset);
